import { afterNextRender, Component, DestroyRef, inject, Injector, runInInjectionContext } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { Observable } from 'rxjs';
import { MainBootstrapComponent } from '@lentau/bootstrap/feature/components/main-bootstrap/main-bootstrap.component';
import { BrowserApiSupportService } from '@lentau/ui-kit/core/services/browser-api-support/browser-api-support.service';
import { EagerCommonBootstrapCSRService } from '@lentau/bootstrap/feature/services/eager-common-bootstrap.csr.service';
import { EagerLentaBootstrapCSRService } from '@lentau/bootstrap/feature/services/eager-lenta-bootstrap.csr.service';
import { AfterInteractCommonBootstrapCSRService } from '@lentau/bootstrap/feature/services/after-interact-common-bootstrap.csr.service';
import { LazyLoadAfterInteractCommonBootstrap } from '@lentau/bootstrap/feature/lazy-load-after-interact-common-bootstrap';
import { LazyLoadCommonBootstrap } from '@lentau/bootstrap/feature/lazy-load-common-bootstrap';
import { executeAfterSiteInteract } from '@lentau/ui-kit/core/helpers/site-interact.helper';
import { RenderTimeService } from '@lentau/core/measure';
import { LazyCommonBootstrapCSRService } from '@lentau/bootstrap/feature/services/lazy-common-bootstrap.csr.service';
import { LazyLoadLentaBootstrap } from '@lentau/bootstrap/feature/lazy-load-lenta-bootstrap';
import { LazyLentaBootstrapCSRService } from '@lentau/bootstrap/feature/services/lazy-lenta-bootstrap.csr.service';

@Component({
  selector: 'monetka-root',
  imports: [RouterOutlet, MainBootstrapComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent {
  private eagerCommonBootstrapCSRService: EagerCommonBootstrapCSRService = inject(EagerCommonBootstrapCSRService);
  private eagerLentaBootstrapCSRService: EagerLentaBootstrapCSRService = inject(EagerLentaBootstrapCSRService);
  private browserApi: BrowserApiSupportService = inject(BrowserApiSupportService);
  private destroyRef: DestroyRef = inject(DestroyRef);
  private renderTimeService: RenderTimeService = inject(RenderTimeService);
  private injector: Injector = inject(Injector);

  private afterInteractCommonBootstrap$: Observable<AfterInteractCommonBootstrapCSRService> =
    LazyLoadAfterInteractCommonBootstrap();
  private lazyLoadCommonBootstrap$: Observable<LazyCommonBootstrapCSRService> = LazyLoadCommonBootstrap();
  private lazyLoadLentaBootstrap$: Observable<LazyLentaBootstrapCSRService> = LazyLoadLentaBootstrap();

  public isBrowser: boolean = this.browserApi.isBrowserAndNotSearchBot();

  constructor() {
    this.renderTimeService.init();

    this.bootstrap();

    afterNextRender(() => {
      runInInjectionContext(this.injector, () => {
        this.lazyBootstrap();
      });
    });
  }

  private bootstrap(): void {
    this.eagerCommonBootstrapCSRService.init();
    this.eagerLentaBootstrapCSRService.init();
  }

  private lazyBootstrap(): void {
    this.lazyLoadCommonBootstrap$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((service: LazyCommonBootstrapCSRService) => {
        runInInjectionContext(this.injector, () => {
          service.init();
        });
      });

    this.lazyLoadLentaBootstrap$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((service: LazyLentaBootstrapCSRService) => {
        runInInjectionContext(this.injector, () => {
          service.init();
        });
      });

    executeAfterSiteInteract(() => {
      this.lazyLoadBootstrap();
    });
  }

  private lazyLoadBootstrap(): void {
    this.afterInteractCommonBootstrap$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((service: AfterInteractCommonBootstrapCSRService) => {
        runInInjectionContext(this.injector, () => {
          service.init();
        });
      });
  }
}
